import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { certificateAPI } from '../../services/api';
import GovernmentCertificateCard from '../public/GovernmentCertificateCard';
import CertificateBuilderWorkspace from './CertificateBuilderWorkspace';
import {
  buildCertificatePreviewContext,
  certificateSchema,
  parseCertificateObject,
} from '../../utils/certificateTemplate';
import './CertificateTemplateDesigner.css';

const STATUS_LABELS = {
  draft: 'कार्यरत मसुदा', pending_approval: 'मंजुरी प्रलंबित', published: 'प्रकाशित',
  rejected: 'दुरुस्तीसाठी परत', retired: 'निवृत्त',
};

const FORMATTERS = ['text', 'uppercase', 'date', 'date_mr', 'date_en', 'number', 'marathi_digits', 'currency', 'percentage', 'gender'];
const templateStatusLabel = template => template?.status === 'published'
  ? template.is_locked ? 'प्रकाशित व लॉक' : 'प्रकाशित · संपादनयोग्य'
  : STATUS_LABELS[template?.status] || template?.status;

const clone = value => JSON.parse(JSON.stringify(value || {}));
const blockId = prefix => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const objectPathValue = (source, path) => String(path).split('.').reduce(
  (value, key) => (value === undefined || value === null ? undefined : value[key]),
  source,
);
const replaceObjectPath = (source, path, value) => {
  const next = clone(source);
  const parts = String(path).split('.').filter(Boolean);
  let cursor = next;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) cursor[part] = value;
    else {
      const nextPart = parts[index + 1];
      cursor[part] = cursor[part] && typeof cursor[part] === 'object'
        ? cursor[part]
        : (/^\d+$/.test(nextPart) ? [] : {});
      cursor = cursor[part];
    }
  });
  return next;
};

const ColorField = ({ label, value, onChange }) => (
  <label className="certificate-color-field">
    {label}
    <span>
      <input type="color" value={value || '#ffffff'} onChange={event => onChange(event.target.value)} />
      <input value={value || ''} maxLength={7} pattern="#[0-9a-fA-F]{6}" onChange={event => onChange(event.target.value)} />
    </span>
  </label>
);

const ToggleField = ({ label, checked, onChange, enabledByDefault = true }) => (
  <label className="certificate-toggle-field">
    <input type="checkbox" checked={enabledByDefault ? checked !== false : checked === true} onChange={event => onChange(event.target.checked)} />
    <span>{label}</span>
  </label>
);

const emptyParagraph = () => ({
  id: blockId('paragraph'), type: 'paragraph', text_mr: 'नवीन मराठी मजकूर', text_en: 'New English paragraph', tone: 'default',
});

const emptyGrid = variable => ({
  id: blockId('fields'), type: 'field_grid', columns: 2,
  items: [{ path: variable?.path || 'subject.name', label_mr: variable?.label || 'तपशील', label_en: variable?.label || 'Details', format: variable?.type === 'date' ? 'date' : 'text', optional: false }],
});

const emptyPartyPanel = variables => {
  const findPath = (preferred, fallback = 'subject.name') => variables.find(variable => variable.path === preferred)?.path || fallback;
  return {
    id: blockId('parties'),
    type: 'party_panel',
    left: {
      label_mr: 'वर', label_en: 'Husband',
      name_path: findPath('fields.groom_name'),
      photo_path: findPath('fields.groom_photo_url', ''),
    },
    right: {
      label_mr: 'वधू', label_en: 'Wife',
      name_path: findPath('fields.bride_name'),
      photo_path: findPath('fields.bride_photo_url', ''),
    },
  };
};

const isPhotoVariable = variable => variable?.type === 'image' || String(variable?.path || '').endsWith('_photo_url');

const emptyPhoto = (variables, sourceType = 'field', selectedVariable = null) => {
  const variable = selectedVariable || variables.find(isPhotoVariable);
  const dynamicPhoto = sourceType === 'field';
  return {
    id: blockId('photo'),
    type: 'photo',
    source_type: sourceType,
    photo_path: dynamicPhoto ? variable?.path || '' : '',
    image_url: '',
    caption_mr: dynamicPhoto ? variable?.label || 'अर्जदाराचा फोटो' : '',
    caption_en: dynamicPhoto ? variable?.label_en || 'Applicant photograph' : '',
    alignment: 'center',
    size: 'medium',
    fit: 'cover',
    show_border: sourceType === 'field',
    optional: false,
  };
};

const defaultBlockHeight = block => {
  if (block.type === 'field_grid') {
    const cols = Math.max(1, Number(block.columns) || (block.items?.length > 1 ? 2 : 1));
    const rows = Math.ceil((block.items?.length || 1) / cols);
    return Math.max(40, rows * 40);
  }
  if (block.type === 'party_panel') return 110;
  if (block.type === 'photo') return 120;
  return block.type === 'notice' ? 70 : 50;
};

const preferredBlockWidth = block => block.type === 'photo'
  ? 140
  : block.type === 'party_panel' || (block.type === 'field_grid' && Number(block.columns) === 2 && (block.items?.length || 1) > 2)
    ? 740
    : 360;

const layoutsOverlap = (left, right, gap = 8) => !(
  left.x + left.width + gap <= right.x
  || right.x + right.width + gap <= left.x
  || left.y + left.height + gap <= right.y
  || right.y + right.height + gap <= left.y
);

const nextFreeLayout = (blocks, block, placement = null, canvasHeight = 520) => {
  const height = defaultBlockHeight(block);
  const width = preferredBlockWidth(block);
  if (placement) return {
    x: Math.max(0, Math.min(740 - width, Number(placement.x) || 0)),
    y: Math.max(0, Math.min(canvasHeight - height, Number(placement.y) || 0)),
    width,
    height,
    z_index: blocks.length,
  };
  const occupied = blocks.map(item => item.layout).filter(Boolean).map(layout => ({
    x: Number(layout.x) || 0,
    y: Number(layout.y) || 0,
    width: Number(layout.width) || 80,
    height: Number(layout.height) || 40,
  }));
  for (let y = 0; y <= canvasHeight - height; y += 10) {
    for (let x = 0; x <= 740 - width; x += 10) {
      const candidate = { x, y, width, height };
      if (!occupied.some(layout => layoutsOverlap(candidate, layout))) return { ...candidate, z_index: blocks.length };
    }
  }
  return null;
};

const errorText = error => {
  const response = error.response?.data;
  const summary = response?.error || error.message || 'कारवाई पूर्ण झाली नाही.';
  const details = Array.isArray(response?.details)
    ? response.details.map(item => item.message).filter(Boolean).slice(0, 3)
    : [];
  return details.length ? `${summary}: ${details.join('; ')}` : summary;
};

function CertificateTemplateDesigner({ tenantSlug, serviceType, serviceSaved, serviceProvider = 'grampanchayat' }, ref) {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [activeTextTarget, setActiveTextTarget] = useState(null);
  const [activeLayoutTextPath, setActiveLayoutTextPath] = useState(null);
  const [activeSection, setActiveSection] = useState('body');
  const [previewScale, setPreviewScale] = useState(0.72);

  const load = useCallback(async preferredId => {
    if (!tenantSlug || !serviceType || !serviceSaved || serviceProvider !== 'grampanchayat') return;
    setBusy(true);
    try {
      const response = await certificateAPI.getTemplates(tenantSlug, { certificate_type: serviceType });
      const nextTemplates = response.data?.templates || [];
      const nextVariables = response.data?.variables?.[serviceType] || [];
      setTemplates(nextTemplates);
      setVariables(nextVariables);
      const preferred = nextTemplates.find(item => String(item.id) === String(preferredId));
      const working = nextTemplates.find(item => ['draft', 'pending_approval', 'rejected'].includes(item.status)
        || (item.status === 'published' && item.is_locked === false));
      const published = nextTemplates.find(item => item.status === 'published');
      const next = preferred || working || published || nextTemplates[0] || null;
      setSelectedId(next ? String(next.id) : '');
      setDraft(next ? { ...clone(next), template_schema: clone(certificateSchema(next.template_schema)) } : null);
      setActiveTextTarget(null);
      setActiveLayoutTextPath(null);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  }, [serviceProvider, serviceSaved, serviceType, tenantSlug]);

  useEffect(() => { load(); }, [load]);

  const publishedUnlocked = draft?.status === 'published' && draft.is_locked === false;
  const editable = draft && (['draft', 'rejected'].includes(draft.status) || publishedUnlocked);
  const isStatutory = draft?.template_kind === 'statutory';
  const workingVersion = templates.find(item => ['draft', 'pending_approval', 'rejected'].includes(item.status)
    || (item.status === 'published' && item.is_locked === false));
  const previewContext = useMemo(() => buildCertificatePreviewContext({ variables, serviceType }), [serviceType, variables]);

  const chooseTemplate = event => {
    const item = templates.find(template => String(template.id) === event.target.value);
    setSelectedId(event.target.value);
    setDraft(item ? { ...clone(item), template_schema: clone(certificateSchema(item.template_schema)) } : null);
    setActiveTextTarget(null);
    setActiveLayoutTextPath(null);
    setMessage(null);
  };

  const setSchema = updater => setDraft(current => ({
    ...current,
    template_schema: typeof updater === 'function' ? updater(clone(current.template_schema)) : updater,
  }));

  const updateSchemaValue = (path, value) => setSchema(schema => {
    const next = replaceObjectPath(schema, path, value);
    if (path === 'blocks' || path.startsWith('blocks.') || path === 'body.layout_mode' || path === 'body.canvas_height') {
      next.body = { ...(next.body || {}), presentation_mode: 'builder' };
    }
    return next;
  });

  const updateRegionBlocks = (region, updater) => setSchema(schema => {
    const blocks = Array.isArray(objectPathValue(schema, region)) ? [...objectPathValue(schema, region)] : [];
    const next = replaceObjectPath(schema, region, updater(blocks));
    if (region === 'blocks') next.body = { ...(next.body || {}), presentation_mode: 'builder' };
    return next;
  });

  const addLayoutBlock = (region, type, options = {}) => {
    const supportedRegions = ['blocks', 'header.content_blocks', 'footer.content_blocks'];
    if (!supportedRegions.includes(region)) return null;
    const block = type === 'field' || type === 'field_grid'
      ? emptyGrid(variables[0])
      : type === 'party_panel'
        ? emptyPartyPanel(variables)
        : type === 'photo'
          ? emptyPhoto(variables, options.sourceType || 'field', options.variable)
        : { ...emptyParagraph(), type, tone: type === 'notice' ? 'notice' : 'default' };
    if (type === 'field') block.columns = 1;
    if (['paragraph', 'notice'].includes(block.type)) {
      if (options.textMr !== undefined) block.text_mr = options.textMr;
      if (options.textEn !== undefined) block.text_en = options.textEn;
    }
    if (options.variable) {
      if (block.type === 'field_grid') block.items = [{ ...block.items[0], path: options.variable.path, label_mr: options.variable.label, label_en: options.variable.label_en || options.variable.label, format: options.variable.type === 'date' ? 'date' : 'text' }];
      if (block.type === 'photo') block.photo_path = options.variable.path;
    }
    if (options.imageUrl) block.image_url = options.imageUrl;
    const currentBlocks = Array.isArray(objectPathValue(draft?.template_schema, region))
      ? objectPathValue(draft.template_schema, region)
      : [];
    if (currentBlocks.length >= (region === 'blocks' ? 30 : 10)) return null;
    const regionCanvasHeight = region.startsWith('header') ? 180 : region.startsWith('footer') ? 140 : (Number(draft.template_schema?.body?.canvas_height) || 520);
    const defaultPlacement = options.placement || (region === 'header.content_blocks' ? { x: 570, y: 10 } : null);
    block.layout = nextFreeLayout(currentBlocks, block, defaultPlacement, regionCanvasHeight);
    updateRegionBlocks(region, blocks => [...blocks, block]);
    return block.id;
  };

  const moveLayoutBlock = (region, from, to) => {
    updateRegionBlocks(region, blocks => {
      if (from < 0 || to < 0 || from >= blocks.length || to >= blocks.length || from === to) return blocks;
      const [moved] = blocks.splice(from, 1);
      blocks.splice(to, 0, moved);
      return blocks;
    });
  };

  const removeLayoutBlock = (region, index) => updateRegionBlocks(region, blocks => blocks.filter((_block, itemIndex) => itemIndex !== index));

  const updateHeader = (key, value) => setSchema(schema => ({
    ...schema, header: { ...(schema.header || {}), [key]: value },
  }));

  const updateHeaderLayout = (itemKey, layout) => setSchema(schema => ({
    ...schema,
    header: {
      ...(schema.header || {}),
      layout: {
        ...(schema.header?.layout || {}),
        [itemKey]: layout,
      },
    },
  }));

  const updateBlock = (index, updater) => setSchema(schema => {
    const blocks = [...(schema.blocks || [])];
    blocks[index] = typeof updater === 'function' ? updater(clone(blocks[index])) : updater;
    return { ...schema, blocks, body: { ...(schema.body || {}), presentation_mode: 'builder' } };
  });

  const updatePartySide = (blockIndex, side, key, value) => updateBlock(blockIndex, block => ({
    ...block,
    [side]: { ...(block[side] || {}), [key]: value },
  }));

  const removeBlock = index => removeLayoutBlock('blocks', index);

  const moveBlock = (from, to) => {
    if (from === null || from === to || to < 0) return;
    moveLayoutBlock('blocks', from, to);
  };

  const insertVariable = path => {
    if (activeLayoutTextPath) {
      const current = objectPathValue(draft?.template_schema, activeLayoutTextPath) || '';
      updateSchemaValue(activeLayoutTextPath, `${current}${current ? ' ' : ''}{{${path}}}`);
      return;
    }
    if (!activeTextTarget) {
      setMessage({ type: 'info', text: 'प्रथम layout मधील outlined मजकूर किंवा body परिच्छेद निवडा.' });
      return;
    }
    updateBlock(activeTextTarget.index, block => ({
      ...block,
      [activeTextTarget.key]: `${block[activeTextTarget.key] || ''} {{${path}}}`,
    }));
  };

  const createDraft = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await certificateAPI.createTemplateDraft(tenantSlug, {
        certificate_type: serviceType,
        change_summary: 'Certificate layout update',
      });
      setMessage({ type: 'success', text: 'नवीन आवृत्तीचा मसुदा तयार झाला.' });
      await load(response.data?.template?.id);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    } finally { setBusy(false); }
  };

  const openOrCreateWorkingVersion = () => {
    if (!workingVersion) {
      createDraft();
      return;
    }
    setSelectedId(String(workingVersion.id));
    setDraft({ ...clone(workingVersion), template_schema: clone(certificateSchema(workingVersion.template_schema)) });
    setMessage(null);
  };

  const persistCurrentDraft = async () => {
    const response = await certificateAPI.updateTemplateDraft(tenantSlug, draft.id, {
      record_version: draft.record_version,
      name_mr: draft.name_mr,
      name_en: draft.name_en,
      change_summary: draft.change_summary,
      template_schema: draft.template_schema,
    });
    const savedTemplate = response.data?.template;
    if (!savedTemplate?.id || savedTemplate.record_version === undefined) {
      throw new Error('The saved certificate version was not returned. Please try again.');
    }
    const persistedDraft = {
      ...draft,
      ...savedTemplate,
      template_schema: clone(certificateSchema(savedTemplate.template_schema || draft.template_schema)),
    };
    setDraft(persistedDraft);
    return persistedDraft;
  };

  const saveDraft = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const savedDraft = await persistCurrentDraft();
      const successText = publishedUnlocked ? 'प्रकाशित आवृत्तीतील बदल जतन झाले.' : 'प्रमाणपत्र मसुदा जतन झाला.';
      setMessage({ type: 'success', text: successText });
      await load(savedDraft.id);
      return { success: true, text: successText, draft: savedDraft };
    } catch (error) {
      const err = errorText(error);
      setMessage({ type: 'error', text: err });
      throw error;
    } finally { setBusy(false); }
  };

  useImperativeHandle(ref, () => ({
    saveDraft: async () => {
      if (editable && draft) {
        return await saveDraft();
      }
      return null;
    },
    isEditable: () => Boolean(editable),
    hasDraft: () => Boolean(draft),
  }), [editable, draft, publishedUnlocked, tenantSlug]);

  const submitDraft = async () => {
    setBusy(true);
    setMessage(null);
    try {
      // Persist first so approval always receives the exact version visible on the canvas.
      // Do not reload between these calls: a reload could replace local edits before submit.
      const savedDraft = await persistCurrentDraft();
      const response = await certificateAPI.submitTemplate(tenantSlug, savedDraft.id);
      setMessage({ type: 'success', text: 'मसुदा मंजुरीसाठी तयार आहे. तुम्ही याच खात्यातून तपासून प्रकाशित करू शकता.' });
      await load(response.data?.template?.id);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    } finally { setBusy(false); }
  };

  const decide = async decision => {
    const reason = decision === 'reject' ? window.prompt('दुरुस्तीचे कारण लिहा (किमान ५ अक्षरे):') : '';
    if (decision === 'reject' && !reason) return;
    setBusy(true);
    setMessage(null);
    try {
      const response = await certificateAPI.decideTemplate(tenantSlug, draft.id, { decision, reason });
      setMessage({ type: 'success', text: decision === 'approve' ? 'प्रमाणपत्र नमुना प्रकाशित झाला. अंतिम होईपर्यंत तो संपादित करता येईल.' : 'मसुदा दुरुस्तीसाठी परत पाठवला.' });
      await load(response.data?.template?.id);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    } finally { setBusy(false); }
  };

  const lockVersion = async () => {
    if (!window.confirm('ही आवृत्ती लॉक केल्यानंतर तिचे थेट संपादन करता येणार नाही. अंतिम करून लॉक करायचे?')) return;
    setBusy(true);
    setMessage(null);
    try {
      const savedResponse = await certificateAPI.updateTemplateDraft(tenantSlug, draft.id, {
        record_version: draft.record_version,
        name_mr: draft.name_mr,
        name_en: draft.name_en,
        change_summary: draft.change_summary,
        template_schema: draft.template_schema,
      });
      const savedTemplate = savedResponse.data?.template;
      const response = await certificateAPI.lockTemplate(tenantSlug, draft.id, {
        record_version: savedTemplate?.record_version,
      });
      setMessage({ type: 'success', text: 'प्रमाणपत्र आवृत्ती अंतिम करून लॉक केली.' });
      await load(response.data?.template?.id);
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    } finally { setBusy(false); }
  };

  const addGridItem = blockIndex => updateBlock(blockIndex, block => ({
    ...block,
    items: [...(block.items || []), emptyGrid(variables.find(item => item.source === 'form'))?.items[0]],
  }));

  const updateGridItem = (blockIndex, fieldIndex, key, value) => updateBlock(blockIndex, block => {
    const items = [...(block.items || [])];
    items[fieldIndex] = { ...items[fieldIndex], [key]: value };
    return { ...block, items };
  });

  const uploadTemplateAsset = async file => {
    if (!file) return null;
    if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Choose a PNG or JPEG artwork file no larger than 2MB.' });
      return null;
    }
    setBusy(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await certificateAPI.uploadTemplateAsset(tenantSlug, formData);
      setMessage({ type: 'success', text: 'Certificate artwork uploaded. Save the template to keep it in this layout.' });
      return response.data?.asset || null;
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
      return null;
    } finally {
      setBusy(false);
    }
  };

  if (serviceProvider !== 'grampanchayat') {
    return <section className="certificate-designer-shell certificate-designer-empty">बाह्य विभागाच्या सेवेसाठी ग्रामपंचायत प्रमाणपत्र तयार केले जात नाही.</section>;
  }
  if (!serviceSaved) {
    return <section className="certificate-designer-shell certificate-designer-empty">प्रमाणपत्राचा नमुना तयार करण्यापूर्वी सेवा आणि अर्ज फील्ड जतन करा.</section>;
  }

  return (
    <section className="certificate-designer-shell">
      <div className="certificate-designer-heading">
        <div>
          <span className="certificate-designer-kicker">Certificate layout</span>
          <h3>प्रमाणपत्र रचना व आवृत्ती नियंत्रण</h3>
          <p>अर्जातील फील्ड variables म्हणून वापरा. प्रकाशित आवृत्तीवर जारी झालेले प्रमाणपत्र कायम अपरिवर्तनीय राहते.</p>
        </div>
        <div className="certificate-designer-version-controls">
          <select value={selectedId} onChange={chooseTemplate} disabled={busy || !templates.length}>
            {!templates.length && <option value="">नमुना उपलब्ध नाही</option>}
            {templates.map(item => <option key={item.id} value={item.id}>v{item.version} — {templateStatusLabel(item)}</option>)}
          </select>
          <button
            type="button"
            onClick={openOrCreateWorkingVersion}
            disabled={busy}
            aria-label={workingVersion ? 'Open editable certificate version' : templates.length ? 'Create editable certificate draft' : 'Create first certificate version'}
            title={workingVersion ? 'उपलब्ध संपादनयोग्य आवृत्ती उघडा.' : 'लॉक आवृत्तीवर आधारित नवीन संपादन मसुदा तयार करा.'}
          >
            {workingVersion ? 'संपादनयोग्य आवृत्ती उघडा' : templates.length ? '+ नवीन संपादन आवृत्ती' : '+ पहिली आवृत्ती'}
          </button>
        </div>
      </div>

      {message && <div className={`certificate-designer-message ${message.type}`}>{message.text}</div>}
      {isStatutory && <div className="certificate-designer-message info">हा वैधानिक नमुना आहे. प्रमाणपत्र update परवानगी असलेला ग्रामपंचायत प्रशासक स्वतः बदल, preview, मंजुरी आणि प्रकाशन करू शकतो. अंतिम खात्री झाल्यानंतरच आवृत्ती लॉक करा.</div>}

      {!draft ? (
        <div className="certificate-designer-empty">या सेवेसाठी नमुना नाही. “नवीन आवृत्ती” निवडा.</div>
      ) : editable ? (
        <CertificateBuilderWorkspace
          draft={draft}
          variables={variables}
          previewContext={previewContext}
          busy={busy}
          editable={editable}
          publishedUnlocked={publishedUnlocked}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          updateSchemaValue={updateSchemaValue}
          updateBlock={updateBlock}
          updateGridItem={updateGridItem}
          updatePartySide={updatePartySide}
          addGridItem={addGridItem}
          addLayoutBlock={addLayoutBlock}
          uploadAsset={uploadTemplateAsset}
          saveDraft={saveDraft}
          submitDraft={submitDraft}
          lockVersion={lockVersion}
          updateDraftValue={(key, value) => setDraft(current => ({ ...current, [key]: value }))}
        />
      ) : (
        <div className="certificate-designer-workspace">
          <div className="certificate-designer-editor">
            <div className="certificate-designer-meta">
              <span className={`certificate-template-status ${draft.status} ${draft.is_locked ? 'locked' : 'unlocked'}`}>{templateStatusLabel(draft)}</span>
              <span>v{draft.version}</span>
              <span>{draft.template_kind === 'statutory' ? 'वैधानिक' : draft.template_kind === 'custom' ? 'सानुकूल' : 'मानक'}</span>
            </div>

            {editable ? (
              <>
                <div className="certificate-designer-message info">{publishedUnlocked ? 'ही प्रकाशित आवृत्ती अनलॉक आहे. बदल जतन करून प्रत्यक्ष प्रमाणपत्र तपासा; अंतिम झाल्यावर “जतन करून लॉक करा” निवडा.' : 'ही कार्यरत आवृत्ती कितीही वेळा जतन व preview करता येते. तयार झाल्यावर मंजुरीसाठी पाठवा आणि याच खात्यातून प्रकाशित करा.'}</div>
                <div className="certificate-section-tabs" role="tablist" aria-label="Certificate layout regions">
                  {[
                    ['page', 'Page'], ['header', 'Header'], ['body', 'Body'], ['footer', 'Footer'],
                  ].map(([key, label]) => <button type="button" role="tab" aria-selected={activeSection === key} className={activeSection === key ? 'active' : ''} key={key} onClick={() => setActiveSection(key)}>{label}</button>)}
                </div>

                {activeSection === 'page' && <div className="certificate-section-panel">
                  <h4>Page and template</h4>
                  <div className="certificate-designer-input-grid">
                    <label>नमुना नाव (मराठी)<input value={draft.name_mr || ''} onChange={event => setDraft(current => ({ ...current, name_mr: event.target.value }))} /></label>
                    <label>Template name (English)<input value={draft.name_en || ''} onChange={event => setDraft(current => ({ ...current, name_en: event.target.value }))} /></label>
                    <ColorField label="Accent color" value={draft.template_schema?.page?.accent_color} onChange={value => updateSchemaValue('page.accent_color', value)} />
                    <ColorField label="Page background" value={draft.template_schema?.page?.background_color} onChange={value => updateSchemaValue('page.background_color', value)} />
                    <ColorField label="Page text" value={draft.template_schema?.page?.text_color} onChange={value => updateSchemaValue('page.text_color', value)} />
                    <ColorField label="Border color" value={draft.template_schema?.page?.border_color} onChange={value => updateSchemaValue('page.border_color', value)} />
                    <label>Border width<input type="number" min="0" max="8" value={draft.template_schema?.page?.border_width ?? 2} onChange={event => updateSchemaValue('page.border_width', Number(event.target.value))} /></label>
                    <label>Horizontal padding<input type="number" min="12" max="64" value={draft.template_schema?.page?.horizontal_padding ?? 40} onChange={event => updateSchemaValue('page.horizontal_padding', Number(event.target.value))} /></label>
                    <label className="certificate-designer-wide">बदलाचा सारांश<input value={draft.change_summary || ''} onChange={event => setDraft(current => ({ ...current, change_summary: event.target.value }))} /></label>
                  </div>
                </div>}

                {activeSection === 'header' && <div className="certificate-section-panel">
                  <h4>Header structure and appearance</h4>
                  <div className="certificate-toggle-grid">
                    <ToggleField label="Show header" checked={draft.template_schema?.header?.visible} onChange={value => updateSchemaValue('header.visible', value)} />
                    <ToggleField label="Emblem" checked={draft.template_schema?.header?.show_emblem} onChange={value => updateSchemaValue('header.show_emblem', value)} />
                    <ToggleField label="Government name" checked={draft.template_schema?.header?.show_government_label} onChange={value => updateSchemaValue('header.show_government_label', value)} />
                    <ToggleField label="Authority reference" checked={draft.template_schema?.header?.show_authority_reference} onChange={value => updateSchemaValue('header.show_authority_reference', value)} />
                    <ToggleField label="Form label" checked={draft.template_schema?.header?.show_form_label} onChange={value => updateSchemaValue('header.show_form_label', value)} />
                    <ToggleField label="Certificate title" checked={draft.template_schema?.header?.show_title} onChange={value => updateSchemaValue('header.show_title', value)} />
                    <ToggleField label="Legal reference" checked={draft.template_schema?.header?.show_legal_reference} onChange={value => updateSchemaValue('header.show_legal_reference', value)} />
                    <ToggleField label="Certificate number" checked={draft.template_schema?.header?.show_certificate_number} onChange={value => updateSchemaValue('header.show_certificate_number', value)} />
                  </div>
                  <div className="certificate-designer-input-grid">
                    <label>Alignment<select value={draft.template_schema?.header?.alignment || 'center'} onChange={event => updateSchemaValue('header.alignment', event.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
                    <label>Emblem source<select value={draft.template_schema?.header?.emblem_source || 'ashoka'} onChange={event => updateSchemaValue('header.emblem_source', event.target.value)}><option value="ashoka">Ashoka emblem</option><option value="tenant_logo">Gram Panchayat logo</option><option value="custom">Custom uploaded image</option></select></label>
                    {draft.template_schema?.header?.emblem_source === 'custom' && <label className="certificate-designer-wide">Custom emblem path<input value={draft.template_schema?.header?.emblem_url || ''} placeholder="/uploads/... or /certificate-assets/..." onChange={event => updateSchemaValue('header.emblem_url', event.target.value)} /></label>}
                    <ColorField label="Header background" value={draft.template_schema?.header?.background_color} onChange={value => updateSchemaValue('header.background_color', value)} />
                    <ColorField label="Header text" value={draft.template_schema?.header?.text_color} onChange={value => updateSchemaValue('header.text_color', value)} />
                    <ColorField label="Title color" value={draft.template_schema?.header?.title_color} onChange={value => updateSchemaValue('header.title_color', value)} />
                    {[
                      ['government_label_mr', 'शासन नाव (मराठी)'], ['government_label_en', 'Government name (English)'],
                      ['title_mr', 'शीर्षक (मराठी)'], ['title_en', 'Title (English)'],
                      ['form_label_mr', 'नमुना / नोंद नाव'], ['form_label_en', 'Form / register label'],
                      ['authority_reference_mr', 'शासन / राजपत्र संदर्भ'], ['authority_reference_en', 'Authority reference (English)'],
                      ['legal_reference_mr', 'कायदेशीर संदर्भ (मराठी)'], ['legal_reference_en', 'Legal reference (English)'],
                    ].map(([key, label]) => <label key={key}>{label}<input value={draft.template_schema?.header?.[key] || ''} onChange={event => updateHeader(key, event.target.value)} /></label>)}
                  </div>
                  <div className="certificate-add-blocks"><button type="button" onClick={() => addLayoutBlock('header.content_blocks', 'paragraph')}>+ Header text</button><button type="button" onClick={() => addLayoutBlock('header.content_blocks', 'notice')}>+ Header notice</button></div>
                </div>}

                {activeSection === 'body' && <div className="certificate-section-panel">
                  <h4>Body content and appearance</h4>
                  <div className="certificate-toggle-grid">
                    <ToggleField
                      label="Show field separator lines"
                      checked={draft.template_schema?.body?.show_field_borders}
                      enabledByDefault={false}
                      onChange={value => updateSchemaValue('body.show_field_borders', value)}
                    />
                  </div>
                  <div className="certificate-designer-input-grid">
                    <label>Text alignment<select value={draft.template_schema?.body?.alignment || 'left'} onChange={event => updateSchemaValue('body.alignment', event.target.value)}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
                    <label>Block spacing<input type="number" min="0" max="32" value={draft.template_schema?.body?.block_gap ?? 9} onChange={event => updateSchemaValue('body.block_gap', Number(event.target.value))} /></label>
                    <ColorField label="Body background" value={draft.template_schema?.body?.background_color} onChange={value => updateSchemaValue('body.background_color', value)} />
                    <ColorField label="Primary text" value={draft.template_schema?.body?.text_color} onChange={value => updateSchemaValue('body.text_color', value)} />
                    <ColorField label="Secondary text" value={draft.template_schema?.body?.secondary_text_color} onChange={value => updateSchemaValue('body.secondary_text_color', value)} />
                    <ColorField label="Variable values" value={draft.template_schema?.body?.variable_color} onChange={value => updateSchemaValue('body.variable_color', value)} />
                    <ColorField label="Field background" value={draft.template_schema?.body?.field_background_color} onChange={value => updateSchemaValue('body.field_background_color', value)} />
                    <ColorField label="Field / photo border color" value={draft.template_schema?.body?.field_border_color} onChange={value => updateSchemaValue('body.field_border_color', value)} />
                  </div>
                </div>}

                {activeSection === 'footer' && <div className="certificate-section-panel">
                  <h4>Footer structure and appearance</h4>
                  <div className="certificate-toggle-grid">
                    <ToggleField label="Show footer" checked={draft.template_schema?.footer?.visible} onChange={value => updateSchemaValue('footer.visible', value)} />
                    <ToggleField label="Place and date" checked={draft.template_schema?.footer?.show_place_date} onChange={value => updateSchemaValue('footer.show_place_date', value)} />
                    <ToggleField label="QR verification" checked={draft.template_schema?.footer?.show_qr} onChange={value => updateSchemaValue('footer.show_qr', value)} />
                    <ToggleField label="Left signature" checked={draft.template_schema?.footer?.show_left_signature} onChange={value => updateSchemaValue('footer.show_left_signature', value)} />
                    <ToggleField label="Right signature" checked={draft.template_schema?.footer?.show_right_signature} onChange={value => updateSchemaValue('footer.show_right_signature', value)} />
                    <ToggleField label="Verification text" checked={draft.template_schema?.footer?.show_verification} onChange={value => updateSchemaValue('footer.show_verification', value)} />
                  </div>
                  <div className="certificate-designer-input-grid">
                    <ColorField label="Footer background" value={draft.template_schema?.footer?.background_color} onChange={value => updateSchemaValue('footer.background_color', value)} />
                    <ColorField label="Footer text" value={draft.template_schema?.footer?.text_color} onChange={value => updateSchemaValue('footer.text_color', value)} />
                    <ColorField label="Signature accent" value={draft.template_schema?.footer?.accent_color} onChange={value => updateSchemaValue('footer.accent_color', value)} />
                    <label>Left signature (Marathi)<input value={draft.template_schema?.signature?.left_label_mr || ''} onChange={event => updateSchemaValue('signature.left_label_mr', event.target.value)} /></label>
                    <label>Left signature (English)<input value={draft.template_schema?.signature?.left_label_en || ''} onChange={event => updateSchemaValue('signature.left_label_en', event.target.value)} /></label>
                    <label>Right signature (Marathi)<input value={draft.template_schema?.signature?.right_label_mr || ''} onChange={event => updateSchemaValue('signature.right_label_mr', event.target.value)} /></label>
                    <label>Right signature (English)<input value={draft.template_schema?.signature?.right_label_en || ''} onChange={event => updateSchemaValue('signature.right_label_en', event.target.value)} /></label>
                    <label className="certificate-designer-wide">Verification text (Marathi)<textarea rows="3" value={draft.template_schema?.footer?.verification_text_mr || ''} onChange={event => updateSchemaValue('footer.verification_text_mr', event.target.value)} /></label>
                    <label className="certificate-designer-wide">Verification text (English)<textarea rows="3" value={draft.template_schema?.footer?.verification_text_en || ''} onChange={event => updateSchemaValue('footer.verification_text_en', event.target.value)} /></label>
                  </div>
                  <div className="certificate-add-blocks"><button type="button" onClick={() => addLayoutBlock('footer.content_blocks', 'paragraph')}>+ Footer text</button><button type="button" onClick={() => addLayoutBlock('footer.content_blocks', 'notice')}>+ Footer notice</button></div>
                </div>}

                <details className="certificate-variable-palette">
                  <summary>
                    <strong>Variables</strong>
                    <span>{variables.length} उपलब्ध · मजकूरात फील्ड मूल्य जोडा</span>
                  </summary>
                  <p>Layout मधील मजकूर किंवा body परिच्छेद निवडून variable जोडा:</p>
                  <div>{variables.map(variable => <button type="button" key={variable.path} title={variable.path} onClick={() => insertVariable(variable.path)}>{variable.label}</button>)}</div>
                </details>

                {activeSection === 'body' && <>
                <div className="certificate-blocks">
                  {(draft.template_schema?.blocks || []).map((block, blockIndex) => (
                    <article
                      className="certificate-block-editor"
                      key={block.id || blockIndex}
                      draggable
                      onDragStart={() => setDragIndex(blockIndex)}
                      onDragOver={event => event.preventDefault()}
                      onDrop={() => { moveBlock(dragIndex, blockIndex); setDragIndex(null); }}
                    >
                      <header>
                        <span className="certificate-drag-handle">⋮⋮</span>
                        <strong>{block.type === 'field_grid' ? 'माहिती फील्ड गट' : block.type === 'party_panel' ? 'दोन व्यक्ती / फोटो पॅनेल' : block.type === 'photo' ? 'एक फोटो' : block.type === 'notice' ? 'सूचना' : 'द्विभाषिक परिच्छेद'}</strong>
                        <div>
                          <button type="button" onClick={() => moveBlock(blockIndex, blockIndex - 1)} disabled={!blockIndex}>↑</button>
                          <button type="button" onClick={() => moveBlock(blockIndex, blockIndex + 1)} disabled={blockIndex === draft.template_schema.blocks.length - 1}>↓</button>
                          <button type="button" className="danger" onClick={() => removeBlock(blockIndex)}>काढा</button>
                        </div>
                      </header>

                      {block.type === 'field_grid' ? (
                        <>
                          <label className="certificate-columns-control">Columns <select value={block.columns || 2} onChange={event => updateBlock(blockIndex, current => ({ ...current, columns: Number(event.target.value) }))}><option value={1}>1</option><option value={2}>2</option></select></label>
                          {(block.items || []).map((item, fieldIndex) => (
                            <div className="certificate-field-editor" key={`${item.path}-${fieldIndex}`}>
                              <select value={item.path || ''} onChange={event => updateGridItem(blockIndex, fieldIndex, 'path', event.target.value)}>
                                {variables.map(variable => <option key={variable.path} value={variable.path}>{variable.label} — {variable.path}</option>)}
                              </select>
                              <input value={item.label_mr || ''} onChange={event => updateGridItem(blockIndex, fieldIndex, 'label_mr', event.target.value)} placeholder="मराठी लेबल" />
                              <input value={item.label_en || ''} onChange={event => updateGridItem(blockIndex, fieldIndex, 'label_en', event.target.value)} placeholder="English label" />
                              <select value={item.format || 'text'} onChange={event => updateGridItem(blockIndex, fieldIndex, 'format', event.target.value)}>{FORMATTERS.map(format => <option key={format}>{format}</option>)}</select>
                              <label><input type="checkbox" checked={Boolean(item.optional)} onChange={event => updateGridItem(blockIndex, fieldIndex, 'optional', event.target.checked)} /> रिक्त असल्यास लपवा</label>
                              <button type="button" className="danger" onClick={() => updateBlock(blockIndex, current => ({ ...current, items: current.items.filter((_entry, index) => index !== fieldIndex) }))}>×</button>
                            </div>
                          ))}
                          <button type="button" className="certificate-add-inline" onClick={() => addGridItem(blockIndex)}>+ फील्ड</button>
                        </>
                      ) : block.type === 'party_panel' ? (
                        <div className="certificate-party-panel-editor">
                          {['left', 'right'].map(side => (
                            <fieldset key={side}>
                              <legend>{side === 'left' ? 'डावी व्यक्ती' : 'उजवी व्यक्ती'}</legend>
                              <input value={block[side]?.label_mr || ''} onChange={event => updatePartySide(blockIndex, side, 'label_mr', event.target.value)} placeholder="मराठी लेबल" />
                              <input value={block[side]?.label_en || ''} onChange={event => updatePartySide(blockIndex, side, 'label_en', event.target.value)} placeholder="English label" />
                              <label>नाव<select value={block[side]?.name_path || ''} onChange={event => updatePartySide(blockIndex, side, 'name_path', event.target.value)}>{variables.filter(variable => variable.type !== 'image').map(variable => <option key={variable.path} value={variable.path}>{variable.label} — {variable.path}</option>)}</select></label>
                              <label>फोटो<select value={block[side]?.photo_path || ''} onChange={event => updatePartySide(blockIndex, side, 'photo_path', event.target.value)}><option value="">फोटो नको</option>{variables.filter(isPhotoVariable).map(variable => <option key={variable.path} value={variable.path}>{variable.label} — {variable.path}</option>)}</select></label>
                            </fieldset>
                          ))}
                        </div>
                      ) : block.type === 'photo' ? (
                        <div className="certificate-photo-block-editor">
                          <label className="certificate-photo-source">फोटो स्रोत<select value={block.photo_path || ''} onChange={event => updateBlock(blockIndex, current => ({ ...current, photo_path: event.target.value }))}>
                            {!variables.some(isPhotoVariable) && <option value="">प्रथम फोटो कागदपत्र किंवा image field कॉन्फिगर करा</option>}
                            {variables.filter(isPhotoVariable).map(variable => <option key={variable.path} value={variable.path}>{variable.label} — {variable.path}</option>)}
                          </select></label>
                          <label>मराठी मथळा<input value={block.caption_mr || ''} maxLength={200} onChange={event => updateBlock(blockIndex, current => ({ ...current, caption_mr: event.target.value }))} /></label>
                          <label>English caption<input value={block.caption_en || ''} maxLength={200} onChange={event => updateBlock(blockIndex, current => ({ ...current, caption_en: event.target.value }))} /></label>
                          <label>मांडणी<select value={block.alignment || 'center'} onChange={event => updateBlock(blockIndex, current => ({ ...current, alignment: event.target.value }))}><option value="left">डावीकडे</option><option value="center">मध्यभागी</option><option value="right">उजवीकडे</option></select></label>
                          <label>आकार<select value={block.size || 'medium'} onChange={event => updateBlock(blockIndex, current => ({ ...current, size: event.target.value }))}><option value="small">लहान</option><option value="medium">मध्यम</option><option value="large">मोठा</option></select></label>
                          <label>फोटो फिट<select value={block.fit || 'cover'} onChange={event => updateBlock(blockIndex, current => ({ ...current, fit: event.target.value }))}><option value="cover">फ्रेम भरा</option><option value="contain">पूर्ण फोटो दाखवा</option></select></label>
                          <label className="certificate-photo-toggle"><input type="checkbox" checked={block.show_border !== false} onChange={event => updateBlock(blockIndex, current => ({ ...current, show_border: event.target.checked }))} /> बॉर्डर दाखवा</label>
                          <label className="certificate-photo-toggle"><input type="checkbox" checked={Boolean(block.optional)} onChange={event => updateBlock(blockIndex, current => ({ ...current, optional: event.target.checked }))} /> फोटो नसल्यास ब्लॉक लपवा</label>
                        </div>
                      ) : (
                        <div className="certificate-paragraph-editor">
                          <textarea value={block.text_mr || ''} onFocus={() => { setActiveLayoutTextPath(null); setActiveTextTarget({ index: blockIndex, key: 'text_mr' }); }} onChange={event => updateBlock(blockIndex, current => ({ ...current, text_mr: event.target.value }))} rows={3} placeholder="मराठी मजकूर" />
                          <textarea value={block.text_en || ''} onFocus={() => { setActiveLayoutTextPath(null); setActiveTextTarget({ index: blockIndex, key: 'text_en' }); }} onChange={event => updateBlock(blockIndex, current => ({ ...current, text_en: event.target.value }))} rows={3} placeholder="English text" />
                        </div>
                      )}
                    </article>
                  ))}
                </div>

                <div className="certificate-add-blocks">
                  <button type="button" onClick={() => addLayoutBlock('blocks', 'paragraph')}>+ परिच्छेद</button>
                  <button type="button" onClick={() => addLayoutBlock('blocks', 'notice')}>+ सूचना</button>
                  <button type="button" onClick={() => addLayoutBlock('blocks', 'field_grid')}>+ फील्ड गट</button>
                  <button type="button" onClick={() => addLayoutBlock('blocks', 'party_panel')}>+ दोन व्यक्ती / फोटो</button>
                  <button type="button" onClick={() => addLayoutBlock('blocks', 'photo')} disabled={!variables.some(isPhotoVariable)}>+ एक फोटो</button>
                </div>
                </>}

                <div className="certificate-designer-actions">
                  <button type="button" onClick={saveDraft} disabled={busy}>{publishedUnlocked ? 'बदल जतन करा' : 'मसुदा जतन करा'}</button>
                  {publishedUnlocked ? (
                    <button type="button" className="lock" onClick={lockVersion} disabled={busy}>🔒 जतन करून लॉक करा</button>
                  ) : (
                    <button type="button" className="primary" onClick={submitDraft} disabled={busy}>मंजुरीसाठी पाठवा</button>
                  )}
                </div>
              </>
            ) : draft.status === 'pending_approval' ? (
              <>
                <div className="certificate-designer-message info">मसुदा तपासा. तयार असल्यास याच खात्यातून प्रकाशित करा; प्रकाशित झाल्यानंतर अंतिम लॉक करेपर्यंत बदल करता येतील.</div>
                <div className="certificate-designer-actions">
                  <button type="button" className="primary" onClick={() => decide('approve')} disabled={busy}>मंजूर करून प्रकाशित करा</button>
                  <button type="button" className="danger" onClick={() => decide('reject')} disabled={busy}>दुरुस्तीसाठी परत</button>
                </div>
              </>
            ) : (
              <div className="certificate-readonly-note">
                {draft.status === 'published' && draft.is_locked
                  ? 'ही प्रकाशित आवृत्ती अंतिम करून लॉक केली आहे. तिचे थेट संपादन करता येणार नाही; बदलांसाठी नवीन संपादन आवृत्ती तयार करा.'
                  : 'ही जुनी किंवा निष्क्रिय आवृत्ती केवळ संदर्भासाठी आहे. सक्रिय आवृत्ती निवडा किंवा नवीन संपादन आवृत्ती तयार करा.'}
              </div>
            )}
          </div>

          <aside className="certificate-designer-preview">
            <div className="certificate-preview-label">
              <span>Live preview · sample data</span>
              <span className="certificate-preview-zoom">
                <button type="button" onClick={() => setPreviewScale(value => Math.max(0.3, Number((value - 0.05).toFixed(2))))} aria-label="Zoom preview out">−</button>
                <strong>{Math.round(previewScale * 100)}%</strong>
                <button type="button" onClick={() => setPreviewScale(value => Math.min(2.5, Number((value + 0.05).toFixed(2))))} aria-label="Zoom preview in">+</button>
                <button type="button" onClick={() => setPreviewScale(0.72)}>Fit</button>
                <button type="button" onClick={() => setPreviewScale(1.0)}>100%</button>
              </span>
            </div>
            <div className="certificate-preview-scale" style={{ '--certificate-preview-scale': previewScale }}>
              <GovernmentCertificateCard
                templateSnapshot={{ schema: parseCertificateObject(draft.template_schema) }}
                dataSnapshot={previewContext}
                issuance={{ template_version: draft.version, revision: 1 }}
                editor={editable ? {
                  builder: true,
                  update: updateSchemaValue,
                  activate: path => { setActiveLayoutTextPath(path); setActiveTextTarget(null); },
                  addBlock: addLayoutBlock,
                  moveBlock: moveLayoutBlock,
                  removeBlock: removeLayoutBlock,
                  updateHeaderLayout,
                  canAddPhoto: variables.some(isPhotoVariable),
                } : null}
              />
            </div>
          </aside>
        </div>
      )}
      {busy && <div className="certificate-designer-loading">लोड होत आहे…</div>}
    </section>
  );
}

export default forwardRef(CertificateTemplateDesigner);
